/**
 * The Notification Center panel (#1093): one unread count, category chips and
 * an "only new" switch, rows grouped by day and bundled per `group_key`.
 * Realtime keeps the list current, so there is no manual refresh.
 *
 * The gear opens the settings view (#1095): per-category preferences and
 * "delete read". Categories switched off for the panel are hidden here and
 * left out of the unread count.
 *
 * The panel floats (#1129): anchored bottom right above the taskbar, inset from
 * the screen edges. Its height is fixed while open (#1133), so filters, bundles
 * and the settings view scroll inside instead of resizing it; the header can
 * maximise it to the full available height. Under the shell-free routes it
 * floats bottom left next to the icon rail instead (`anchor="rail-left"`, #1169).
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNotifications, type UseNotificationsReturn } from '../../hooks/useNotifications'
import { useNotificationPanel, useNotificationsContext } from '../../context/NotificationsContext'
import type { Notification } from '../../services/notification-service'
import { logError } from '@/lib/logger'
import { ArrowLeft, BellOff, Check, CheckCheck, CheckCircle, ListFilter, Loader2, Maximize2, Minimize2, Settings, X } from 'lucide-react'
import {
  buildSections,
  categoryChips,
  type CategoryFilter,
} from '@/lib/utils/notificationGrouping'
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences'
import { visibleNotifications, visibleUnreadCount } from '@/lib/utils/notificationPreferences'
import { NotificationRow } from './notifications/NotificationRow'
import { NotificationSettings } from './notifications/NotificationSettings'
import { useOpenNotification } from './notifications/useOpenNotification'
import {
  ACCENT,
  EASE_STANDARD,
  FLOAT_OFFSET_PX,
  FLOAT_TRANSITION,
  FLOATING_SURFACE,
  FOCUS_RING,
  INSTANT_TRANSITION,
  STATUS,
  readable,
  tint,
} from './notifications/notificationStyles'
import { panelGeometry, readStoredMaximized, storeMaximized, type PanelAnchor } from './notifications/panelSize'

export { PANEL_INSET_PX } from './notifications/panelSize'

interface NotificationCenterProps {
  isOpen: boolean
  onClose: () => void
  controller?: UseNotificationsReturn
  /**
   * The control that toggles the panel (the bell). A press on it is not an
   * outside click — it toggles instead — and closing returns focus to it.
   */
  anchorRef?: RefObject<HTMLElement | null>
  /** Where the panel floats: above the taskbar (default) or beside the standalone rail. */
  anchor?: PanelAnchor
}

/** Maximise and restore resize calmly; the content is laid out against the final size. */
const RESIZE_TRANSITION = `width 180ms cubic-bezier(${EASE_STANDARD.join(',')}), height 180ms cubic-bezier(${EASE_STANDARD.join(',')})`

const ICON_BUTTON = `h-8 w-8 flex items-center justify-center rounded-md text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] disabled:opacity-40 disabled:pointer-events-none ${FOCUS_RING}`

function EmptyState({ filtered }: { filtered: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="my-auto flex flex-col items-center gap-1.5 px-6 py-12 text-center">
      <span
        aria-hidden
        className="h-10 w-10 rounded-full flex items-center justify-center mb-1"
        style={filtered ? tint(STATUS.success) : undefined}
      >
        {filtered ? <CheckCircle className="h-5 w-5" /> : <BellOff className="h-5 w-5 text-[var(--text-secondary)]" />}
      </span>
      <p className="text-sm font-semibold text-[var(--text-color)]">
        {filtered ? t('notificationCenter.emptyFiltered.title') : t('notificationCenter.empty.title')}
      </p>
      <p className="text-xs text-[var(--text-secondary)]">
        {filtered ? t('notificationCenter.emptyFiltered.text') : t('notificationCenter.empty.text')}
      </p>
    </div>
  )
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose, controller, anchorRef, anchor = 'taskbar-right' }) => {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  const panelRef = useRef<HTMLDivElement>(null)
  const contextHook = (() => {
    try { return useNotificationsContext() } catch { return null }
  })()
  const hook = controller || contextHook || useNotifications()
  const { notifications: allNotifications, isLoading, error, markAsRead, markAsUnread, markAllAsRead, deleteNotification, deleteAllRead } =
    hook
  const notificationPreferences = useNotificationPreferences()
  const { preferences } = notificationPreferences
  const notifications = useMemo(
    () => visibleNotifications(allNotifications, preferences),
    [allNotifications, preferences],
  )
  const unreadCount = visibleUnreadCount(hook.unreadCount, allNotifications, preferences)

  // Report to the shared panel state so toasts hold back, whoever opened the panel.
  const panel = useNotificationPanel()
  const panelOpened = panel?.panelOpened
  const panelClosed = panel?.panelClosed
  useEffect(() => {
    if (!isOpen || !panelOpened || !panelClosed) return
    panelOpened()
    return panelClosed
  }, [isOpen, panelOpened, panelClosed])

  const [category, setCategory] = useState<CategoryFilter>('all')
  const [onlyUnread, setOnlyUnread] = useState(false)
  const [view, setView] = useState<'list' | 'settings'>('list')
  const [maximized, setMaximized] = useState(readStoredMaximized)
  const toggleMaximized = () => {
    const next = !maximized
    setMaximized(next)
    storeMaximized(next)
  }

  // Every opening starts at the list.
  useEffect(() => {
    if (!isOpen) setView('list')
  }, [isOpen])

  // Focus moves into the panel when it opens (and to the new view's heading
  // when the view changes). Closing with Escape or the close button returns it
  // to the bell (or, without one, to where it came from); opening a target or
  // clicking outside does not — the focus belongs to what was opened or clicked then.
  const headingRef = useRef<HTMLHeadingElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!isOpen) return
    const opener = document.activeElement
    returnFocusRef.current = opener instanceof HTMLElement && opener !== document.body ? opener : null
  }, [isOpen])
  useEffect(() => {
    if (isOpen) headingRef.current?.focus({ preventScroll: true })
  }, [isOpen, view])
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const dismiss = () => {
    const anchor = anchorRef?.current
    const target = anchor?.isConnected ? anchor : returnFocusRef.current
    onCloseRef.current()
    if (target?.isConnected) target.focus({ preventScroll: true })
  }
  const dismissRef = useRef(dismiss)
  dismissRef.current = dismiss

  // Escape closes the panel wherever the focus is — also after the focused
  // row went away and focus fell to the page. A key another dialog or a row
  // menu already handled (defaultPrevented) is theirs.
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const target = event.target instanceof Element ? event.target : null
      const otherDialog = target?.closest('[role="dialog"], [role="alertdialog"]')
      if (otherDialog && otherDialog !== panelRef.current) return
      dismissRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  const chips = useMemo(() => categoryChips(notifications), [notifications])
  // A chip whose last row was deleted disappears; fall back to "all" rather than an empty list.
  const activeCategory = category === 'all' || chips.some((chip) => chip.category === category) ? category : 'all'
  const sections = useMemo(
    () => buildSections(notifications, { category: activeCategory, onlyUnread }),
    [notifications, activeCategory, onlyUnread],
  )
  // "Delete read" deletes hidden categories' read rows too, so it counts them.
  const hasRead = allNotifications.some((n) => n.is_read)

  // When the focused row leaves the list (marked read under "only new",
  // deleted) and focus fell to the page, it moves to the row now in its
  // place, else the list. Focus that went elsewhere on purpose stays there.
  const listRef = useRef<HTMLDivElement>(null)
  const focusedRowRef = useRef<{ element: Element; index: number } | null>(null)
  const rememberFocusedRow = (event: React.FocusEvent) => {
    const row = (event.target as Element).closest('[data-notification-row]')
    const rows = listRef.current ? Array.from(listRef.current.querySelectorAll('[data-notification-row]')) : []
    focusedRowRef.current = row ? { element: event.target as Element, index: rows.indexOf(row) } : null
  }
  const forgetFocusedRow = (event: React.FocusEvent) => {
    const next = event.relatedTarget as Node | null
    if (next && !listRef.current?.contains(next)) focusedRowRef.current = null
  }
  useLayoutEffect(() => {
    const focused = focusedRowRef.current
    const list = listRef.current
    if (!focused || !list || focused.element.isConnected) return
    focusedRowRef.current = null
    const active = document.activeElement
    if (active && active !== document.body) return
    const rows = Array.from(list.querySelectorAll('[data-notification-row]'))
    const next = rows[Math.min(focused.index, rows.length - 1)]?.querySelector<HTMLElement>('button')
    ;(next ?? list).focus({ preventScroll: true })
  })

  const openNotification = useOpenNotification(notifications, markAsRead)
  const handleOpen = (notification: Notification) => {
    if (openNotification(notification)) onClose()
  }

  // A press outside closes the panel. The anchor is left to its own click,
  // which toggles — closing here first would make that click reopen it.
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target || panelRef.current?.contains(target) || anchorRef?.current?.contains(target)) return
      onCloseRef.current()
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [isOpen, anchorRef])

  const report = (context: string) => (err: unknown) => logError(err as Error, { context: `NotificationCenter.${context}` })

  const handleMarkRead = (items: Notification[]) => {
    for (const n of items) markAsRead(n.id).catch(report('markRead'))
  }
  const handleMarkUnread = (items: Notification[]) => {
    for (const n of items) markAsUnread(n.id).catch(report('markUnread'))
  }
  const handleDelete = (items: Notification[]) => {
    for (const n of items) deleteNotification(n.id).catch(report('delete'))
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby="notification-center-title"
          data-testid="notification-center"
          initial={reduceMotion ? false : { y: FLOAT_OFFSET_PX, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={reduceMotion ? { opacity: 0, transition: INSTANT_TRANSITION } : { y: FLOAT_OFFSET_PX, opacity: 0 }}
          transition={reduceMotion ? INSTANT_TRANSITION : FLOAT_TRANSITION}
          className="fixed z-[15000] flex flex-col overflow-hidden rounded-[14px] border border-[var(--window-border-color)] bg-[var(--window-background)]"
          data-maximized={maximized}
          data-anchor={anchor}
          style={{
            ...panelGeometry(maximized, anchor),
            ...FLOATING_SURFACE,
            transition: reduceMotion ? 'none' : RESIZE_TRANSITION,
          }}
        >
          <header className="flex items-center justify-between pl-[18px] pr-3.5 pt-3.5 pb-2.5">
            <div className="flex items-center gap-2">
              {view === 'settings' && (
                <button
                  type="button"
                  onClick={() => setView('list')}
                  aria-label={t('notificationCenter.settings.back')}
                  title={t('notificationCenter.settings.back')}
                  data-testid="notification-settings-back"
                  className={`${ICON_BUTTON} -ml-2`}
                >
                  <ArrowLeft className="h-[17px] w-[17px]" aria-hidden />
                </button>
              )}
              <h2
                id="notification-center-title"
                ref={headingRef}
                tabIndex={-1}
                className="text-base font-semibold text-[var(--text-color)] rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]"
              >
                {view === 'settings' ? t('notificationCenter.settings.title') : t('notificationCenter.title')}
              </h2>
              {view === 'list' && unreadCount > 0 && (
                <span
                  className="text-xs font-semibold px-2 py-px rounded-full"
                  style={tint(ACCENT)}
                >
                  {t('notificationCenter.unreadBadge', { count: unreadCount })}
                </span>
              )}
            </div>
            <div className="flex gap-0.5">
              {view === 'list' && (
                <button
                  type="button"
                  onClick={() => markAllAsRead().catch(report('markAllRead'))}
                  disabled={unreadCount === 0}
                  aria-label={t('notificationCenter.markAllAsRead')}
                  title={t('notificationCenter.markAllAsRead')}
                  className={ICON_BUTTON}
                >
                  <CheckCheck className="h-[17px] w-[17px]" aria-hidden />
                </button>
              )}
              <button
                type="button"
                onClick={toggleMaximized}
                aria-pressed={maximized}
                aria-label={maximized ? t('notificationCenter.restore') : t('notificationCenter.maximize')}
                title={maximized ? t('notificationCenter.restore') : t('notificationCenter.maximize')}
                data-testid="notification-maximize-toggle"
                className={ICON_BUTTON}
              >
                {maximized
                  ? <Minimize2 className="h-[16px] w-[16px]" aria-hidden />
                  : <Maximize2 className="h-[16px] w-[16px]" aria-hidden />}
              </button>
              {view === 'list' && (
                <>
                  <button
                    type="button"
                    onClick={() => setView('settings')}
                    aria-label={t('notificationCenter.settings.open')}
                    title={t('notificationCenter.settings.open')}
                    data-testid="notification-settings-button"
                    className={ICON_BUTTON}
                  >
                    <Settings className="h-[17px] w-[17px]" aria-hidden />
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={dismiss}
                aria-label={t('common.close')}
                title={t('common.close')}
                className={ICON_BUTTON}
              >
                <X className="h-[17px] w-[17px]" aria-hidden />
              </button>
            </div>
          </header>

          {view === 'settings' ? (
            <NotificationSettings
              preferences={notificationPreferences}
              hasRead={hasRead}
              onDeleteRead={() => deleteAllRead().catch(report('deleteAllRead'))}
            />
          ) : (
          <>

          <div className="flex flex-wrap items-center gap-1.5 px-[18px] pb-3 border-b border-[var(--window-border-color)]">
            {/* `contents`: the category chips and the "only new" pill wrap as one row. */}
            <div role="group" aria-label={t('notificationCenter.filterLabel')} className="contents">
              {[{ category: 'all' as const, unread: 0 }, ...chips].map((chip) => {
                const active = activeCategory === chip.category
                return (
                  <button
                    key={chip.category}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setCategory(chip.category)}
                    data-testid={`notification-category-chip-${chip.category}`}
                    className={`h-7 px-2.5 flex-shrink-0 whitespace-nowrap rounded-full border text-xs transition-colors ${FOCUS_RING} ${
                      active
                        ? 'font-semibold border-[var(--text-color)] bg-[var(--text-color)] text-[var(--window-background)]'
                        : 'border-[var(--window-border-color)] text-[var(--text-color)] hover:bg-[var(--menu-item-hover)]'
                    }`}
                  >
                    {t(`notificationCenter.chips.${chip.category}`)}
                    {chip.unread > 0 && <span className="ml-1 opacity-70">{chip.unread}</span>}
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              aria-pressed={onlyUnread}
              onClick={() => setOnlyUnread((value) => !value)}
              data-testid="notification-only-new-toggle"
              className={`ml-1 h-7 pl-2 pr-2.5 flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-dashed text-xs transition-colors ${FOCUS_RING} ${
                onlyUnread
                  ? 'font-semibold border-solid'
                  : 'border-[var(--window-border-color)] text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-[var(--menu-item-hover)]'
              }`}
              style={onlyUnread ? { ...tint(ACCENT), borderColor: ACCENT } : undefined}
            >
              {onlyUnread
                ? <Check className="h-3.5 w-3.5" aria-hidden />
                : <ListFilter className="h-3.5 w-3.5" aria-hidden />}
              {t('notificationCenter.onlyUnread')}
            </button>
          </div>

          {error && (
            <div
              role="alert"
              className="mx-[18px] mt-3 p-3 rounded-md border"
              style={{
                borderColor: `color-mix(in srgb, ${STATUS.danger} 35%, transparent)`,
                background: `color-mix(in srgb, ${STATUS.danger} 10%, transparent)`,
              }}
            >
              <p className="text-sm" style={{ color: readable(STATUS.danger) }}>{t('notificationCenter.loadError')}</p>
            </div>
          )}

          <div
            ref={listRef}
            tabIndex={-1}
            aria-label={t('notificationCenter.listLabel')}
            onFocus={rememberFocusedRow}
            onBlur={forgetFocusedRow}
            className="flex-auto min-h-0 flex flex-col overflow-y-auto pb-2 focus:outline-none"
          >
            {isLoading && notifications.length === 0 ? (
              <div role="status" className="my-auto flex items-center justify-center gap-2 py-12 text-[var(--text-secondary)]">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                <span className="text-sm">{t('notificationCenter.loading')}</span>
              </div>
            ) : sections.length === 0 ? (
              <EmptyState filtered={notifications.length > 0} />
            ) : (
              sections.map((section) => (
                <section key={section.bucket} aria-labelledby={`notification-day-${section.bucket}`}>
                  <h3
                    id={`notification-day-${section.bucket}`}
                    className="px-[18px] pt-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)]"
                  >
                    {t(`notificationCenter.days.${section.bucket}`)}
                  </h3>
                  <ul>
                    {section.entries.map((entry) => (
                      <NotificationRow
                        key={entry.key}
                        entry={entry}
                        onOpen={handleOpen}
                        onMarkRead={handleMarkRead}
                        onMarkUnread={handleMarkUnread}
                        onDelete={handleDelete}
                      />
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>
          </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
